import { useState, useEffect, useRef } from 'react';
import { useEventCallback } from '../../hooks/use-event-callback';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Separator } from '../../components/ui/separator';
import api, { productsAPI, posAPI, paymentAPI, productLookupAPI, getApiErrorMessage } from '../../lib/api';
import { toast } from 'sonner';
import {
  Search, Plus, Minus, Trash2, ShoppingCart, User, CreditCard, Banknote,
  Printer, QrCode, Scan, Receipt, UserPlus, X, RefreshCcw, FileText, Download,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getImageUrl } from '../../lib/utils';
import { printInvoice as generatePrintInvoice } from '../../utils/printInvoice';

export default function WholesaleSales() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const enforceMinQty = !isAdmin;
  const navigate = useNavigate();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Countdown timer effect for button cooldown
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0 && isButtonDisabled) {
      setIsButtonDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [countdown, isButtonDisabled]);

  // Redirect POS users to their POS page
  useEffect(() => {
    if (user?.role === 'pos_user') {
      toast.error('Access denied. POS users cannot access Wholesale Portal.');
      navigate('/admin/pos');
    }
  }, [user, navigate]);

  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const [limit, setLimit] = useState(20);
  const [inputPage, setInputPage] = useState('');
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [scanInput, setScanInput] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [customer, setCustomer] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [applyGST, setApplyGST] = useState(false);
  const [discountType, setDiscountType] = useState('none'); // none, percentage, flat
  const [discountValue, setDiscountValue] = useState('');
  const [takeawayDiscount, setTakeawayDiscount] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);
  const [printSize, setPrintSize] = useState('a4'); // 'a4' or 'thermal'
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '' });
  const [settings, setSettings] = useState(null);
  const [downloadingCatalog, setDownloadingCatalog] = useState(false);

  const scanInputRef = useRef(null);
  const cartScrollRef = useRef(null); // Ref for auto-scrolling cart container

  const [showFetchDialog, setShowFetchDialog] = useState(false);
  const [quotationId, setQuotationId] = useState('');
  const [loadedQuotationId, setLoadedQuotationId] = useState(null);
  const getWholesalePrice = (product) => Number(
    product?.wholesale_price ?? product?.selling_price ?? 0,
  );
  // Available stock. Non-staff responses may only carry `in_stock`; unknown stock is left to the server check.
  const getStock = (product) => {
    if (typeof product?.stock_qty === 'number') return product.stock_qty;
    return product?.in_stock === false ? 0 : Infinity;
  };
  const getMinQty = (product) => product?.wholesale_min_qty || 10;
  // Read-only card: nothing in stock, or (when min qty is enforced) not enough stock to meet it
  const isUnavailable = (product) => {
    const stock = getStock(product);
    return stock <= 0 || (enforceMinQty && stock < getMinQty(product));
  };

  // Fetch settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/settings/public');
        setSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // New State for Edit/Return
  const [isEditing, setIsEditing] = useState(false);
  const [showReturnDialog, setShowReturnDialog] = useState(false);
  const [returnOrder, setReturnOrder] = useState(null);
  const [returnItems, setReturnItems] = useState({}); // { pid: qty_to_return }
  const [returnQuery, setReturnQuery] = useState('');

  const fetchProducts = useEventCallback(async (page = 1, searchQuery = search) => {
    try {
      const response = await productsAPI.getAll({
        limit: limit,
        page: page,
        for_pos: true,
        include_inactive: false,
        search: searchQuery
      });


      const data = response.data;
      setProducts(data.products || []);
      setTotalPages(data.pages || 1);
      setTotalProducts(data.total || 0);
      setCurrentPage(page);


    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  });

  // Keyboard shortcuts for pagination
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle if not typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            if (currentPage > 1) {
              fetchProducts(currentPage - 1);
            }
            break;
          case 'ArrowRight':
            e.preventDefault();
            if (currentPage < totalPages) {
              fetchProducts(currentPage + 1);
            }
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, totalPages, fetchProducts]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProducts(1, search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search, fetchProducts]);

  // Fetch products when limit changes
  useEffect(() => {
    fetchProducts(1);
  }, [limit, fetchProducts]);

  const fetchQuotation = async () => {
    if (!quotationId) return;
    setLoading(true);
    try {
      let order;
      try {
        const response = await api.get(`/orders/${quotationId}/quotation`);
        order = response.data;
      } catch (e) {
        // Fallback to standard admin order GET
        const res = await api.get(`/admin/orders/${quotationId}`);
        order = res.data;
      }

      // Populate cart
      const newCart = await Promise.all(order.items.map(async item => {
        let currentProduct = {};
        try {
          const productResponse = await productsAPI.getOne(item.product_id);
          currentProduct = productResponse.data || {};
        } catch (_) {
          // Use the quotation snapshot if the product is no longer available.
        }
        return { product: {
          ...currentProduct,
          id: item.product_id,
          name: item.product_name,
          sku: item.sku,
          selling_price: currentProduct.selling_price ?? item.price,
          wholesale_price: currentProduct.wholesale_price ?? item.price,
          images: item.image_url ? [item.image_url] : [],
          gst_rate: item.gst_rate || 18,
          // Real stock; a completed order already deducted its own quantity, so add it back for editing.
          // Unknown stock (product fetch failed) is left undefined and checked by the server.
          stock_qty: typeof currentProduct.stock_qty === 'number'
            ? currentProduct.stock_qty + (order.status === 'quotation' ? 0 : item.quantity)
            : undefined,
        },
        quantity: item.quantity
        };
      }));
      setCart(newCart);

      // Populate customer
      if (order.customer_phone) {
        setCustomerQuery(order.customer_phone);
        // Ideally fetch customer object too
        const customerRes = await api.get(`/admin/pos/search-customer?query=${order.customer_phone}`);
        if (customerRes.data) setCustomer(customerRes.data);
      }

      // Populate discount
      if (order.discount_percentage > 0) {
        setDiscountType('percentage');
        setDiscountValue(order.discount_percentage);
      } else if (order.discount_amount > 0) {
        setDiscountType('flat');
        setDiscountValue(order.discount_amount);
      } else {
        setDiscountType('none');
        setDiscountValue('');
      }

      setApplyGST(order.gst_applied);

      if (order.status === 'completed' || order.status === 'delivered') {
        setIsEditing(true);
        setLoadedQuotationId(order.id);
        toast.success(`Order #${order.order_number || 'Loaded'} loaded for editing`);
      } else {
        setIsEditing(false);
        setLoadedQuotationId(order.id);
        toast.success('Quotation loaded successfully');
      }

      setShowFetchDialog(false);
      setQuotationId('');

    } catch (error) {
      toast.error('Failed to fetch quotation');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProducts(1, search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search, fetchProducts]);

  // Fetch products when limit changes
  useEffect(() => {
    fetchProducts(1);
  }, [limit, fetchProducts]);

  const searchCustomer = async () => {
    if (!customerQuery || customerQuery.trim().length < 3) {
      toast.error('Please enter at least 3 characters to search');
      return;
    }


    try {
      const response = await posAPI.searchCustomer(customerQuery.trim());

      if (response.data && response.data !== null) {
        setCustomer(response.data);
        toast.success(`Customer found: ${response.data.name}`);
      } else {
        toast.info('Customer not found. You can add them.');
        setShowAddCustomer(true);
        // If it looks like a phone number, set it as phone, otherwise as name
        const isPhoneNumber = /^\d{10,}$/.test(customerQuery.trim());
        setNewCustomer({
          ...newCustomer,
          phone: isPhoneNumber ? customerQuery.trim() : '',
          name: !isPhoneNumber ? customerQuery.trim() : ''
        });
      }
    } catch (error) {
      console.error('Customer search error:', error);
      console.error('Error response:', error.response);

      if (error.response) {
        toast.error(`Search failed: ${error.response.data?.detail || error.response.statusText}`);
      } else if (error.request) {
        toast.error('No response from server. Please check if the backend is running.');
      } else {
        toast.error('Search request failed');
      }

      setCustomer(null);
      setShowAddCustomer(true);
      // If it looks like a phone number, set it as phone, otherwise as name
      const isPhoneNumber = /^\d{10,}$/.test(customerQuery.trim());
      setNewCustomer({
        ...newCustomer,
        phone: isPhoneNumber ? customerQuery.trim() : '',
        name: !isPhoneNumber ? customerQuery.trim() : ''
      });
    }
  };

  const handleScanInput = async (e) => {
    if (e.key === 'Enter' && scanInput) {
      const term = scanInput.trim();
      const termUpper = term.toUpperCase();

      // 1. Fast path: check currently loaded page (case-insensitive)
      const localProduct = products.find(p =>
        p.sku?.toUpperCase() === termUpper ||
        (p.upc_code && p.upc_code.toUpperCase() === termUpper) ||
        p.name?.toUpperCase() === termUpper
      );

      if (localProduct) {
        addToCart(localProduct);
        setScanInput('');
        return;
      }

      // 2. Fallback: global API lookup — works for any page
      const toastId = toast.loading(`Searching for: ${term}...`);
      try {
        const response = await productLookupAPI.bySku(term);
        if (response.data) {
          addToCart(response.data);
          toast.success(`Added: ${response.data.name}`, { id: toastId });
        } else {
          toast.error('Product not found', { id: toastId });
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) {
          toast.error(`No product found for: ${term}`, { id: toastId });
        } else {
          toast.error('Scanner error — please try again', { id: toastId });
        }
      }
      setScanInput('');
    }
  };

  const addToCart = (product) => {
    const stock = getStock(product);
    if (stock <= 0) {
      toast.error('Product out of stock');
      return;
    }

    const minQty = getMinQty(product);
    if (enforceMinQty && stock < minQty) {
      toast.error(`Minimum wholesale qty is ${minQty}, but only ${stock} in stock`);
      return;
    }

    const existing = cart.find(item => item.product.id === product.id);
    if (existing && existing.quantity >= stock) {
      toast.error(`Only ${stock} in stock`);
      return;
    }
    // Start at min qty; admin (no min qty) is capped at what is in stock.
    const startQty = Math.min(minQty, stock);

    setCart(prev => {
      if (prev.some(item => item.product.id === product.id)) {
        return prev.map(item =>
          item.product.id === product.id
            ? { ...item, quantity: Math.min(item.quantity + 1, stock) }
            : item
        );
      }
      return [...prev, { product, quantity: startQty }];
    });

    if (!existing) {
      toast.success(startQty < minQty
        ? `Added ${startQty} items (only ${stock} in stock, min qty ${minQty})`
        : `Added ${startQty} items (Min Qty)`);
    }

    // Auto-scroll to bottom of cart after adding item
    setTimeout(() => {
      if (cartScrollRef.current) {
        cartScrollRef.current.scrollTop = cartScrollRef.current.scrollHeight;
      }
    }, 50);
  };

  const updateQuantity = (productId, quantity) => {
    const item = cart.find(i => i.product.id === productId);
    const minQty = item?.product.wholesale_min_qty || 10;

    if (!item) return;

    if (!enforceMinQty && quantity <= 0) {
      setCart(prev => prev.filter(cartItem => cartItem.product.id !== productId));
      return;
    }

    if (enforceMinQty && quantity < minQty) {
      toast.error(`Minimum quantity is ${minQty}`);
      return;
    }

    const stock = getStock(item.product);
    if (quantity > stock) {
      toast.error(`Only ${stock} in stock`);
      return;
    }

    setCart(prev =>
      prev.map(item =>
        item.product.id === productId
          ? { ...item, quantity }
          : item
      )
    );
  };

  const removeFromCart = (productId) => {
    const item = cart.find(i => i.product.id === productId);
    const minQty = item?.product.wholesale_min_qty || 10;
    if (enforceMinQty && item && item.quantity <= minQty) {
      toast.error(`Cannot reduce below minimum quantity ${minQty}`);
      return;
    }
    setCart(prev => prev.filter(cartItem => cartItem.product.id !== productId));
  };

  const getSubtotal = () => cart.reduce((sum, item) => {
    // ALWAYS use wholesale price on this page
    const effectivePrice = getWholesalePrice(item.product);
    return sum + effectivePrice * item.quantity;
  }, 0);

  const getGST = () => applyGST ? getSubtotal() * 0.18 : 0;

  const getRequestedDiscount = () => {
    if (discountType === 'percentage' && discountValue) {
      return getSubtotal() * (parseFloat(discountValue) / 100);
    } else if (discountType === 'flat' && discountValue) {
      return parseFloat(discountValue);
    }
    return 0;
  };

  const getDiscountDetails = () => {
    const subtotal = getSubtotal();
    const requestedDiscount = Math.max(getRequestedDiscount(), 0);
    const safeRequested = Math.min(requestedDiscount, subtotal);
    if (safeRequested <= 0 || subtotal <= 0 || cart.length === 0) {
      return { totalDiscount: 0, perItemDiscount: {}, maxAllowedDiscount: 0 };
    }

    const lines = cart.map((item) => {
      const price = getWholesalePrice(item.product);
      const lineTotal = Number(price) * Number(item.quantity);
      const maxDiscountPct = item.product.max_discount_amount;
      const lineCap = maxDiscountPct !== null && maxDiscountPct !== undefined && Number(maxDiscountPct) >= 0
        ? Math.min(lineTotal, lineTotal * (Number(maxDiscountPct) / 100))
        : lineTotal;
      return { productId: item.product.id, lineTotal, lineCap };
    });

    const maxAllowedDiscount = lines.reduce((sum, line) => sum + line.lineCap, 0);

    // Each item gets exactly its proportional share of the requested discount,
    // capped at its own max_discount_amount. No redistribution of leftovers.
    const discountPct = safeRequested / subtotal;
    const perItemDiscount = {};
    lines.forEach((line) => {
      const proportional = line.lineTotal * discountPct;
      perItemDiscount[line.productId] = Math.min(proportional, line.lineCap);
    });

    return {
      totalDiscount: Math.max(0, Object.values(perItemDiscount).reduce((sum, v) => sum + v, 0)),
      perItemDiscount,
      maxAllowedDiscount,
    };
  };

  const getDiscount = () => getDiscountDetails().totalDiscount;

  const getTakeawayAmount = () => parseFloat(takeawayDiscount) || 0;

  const getTakeawayDetails = () => {
    const totalTakeaway = getTakeawayAmount();
    const subtotal = getSubtotal();
    const perItemTakeaway = {};
    if (totalTakeaway > 0 && subtotal > 0 && cart.length > 0) {
      cart.forEach(item => {
        const price = getWholesalePrice(item.product);
        const lineTotal = Number(price) * Number(item.quantity);
        perItemTakeaway[item.product.id] = (lineTotal / subtotal) * totalTakeaway;
      });
    }
    return { totalTakeaway, perItemTakeaway };
  };

  const getTotal = () => getSubtotal() + getGST() - getDiscount() - getTakeawayAmount();
  const discountDetails = getDiscountDetails();

  const generateQR = async () => {
    try {
      const orderNumber = `POS${Date.now()}`;
      const customerName = customer?.name || customerQuery || 'Customer';
      const response = await paymentAPI.getQR(getTotal(), customerName, orderNumber);
      setQrData(response.data);
      setShowQRDialog(true);
    } catch (error) {
      console.error('QR Generation Error:', error);
      toast.error('Failed to generate QR');
    }
  };

  const downloadCatalog = async () => {
    setDownloadingCatalog(true);
    try {
      const toastId = toast.loading("Generating catalog PDF...");
      const response = await productsAPI.downloadCatalog(true); // Pass true for wholesale pricing

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `wholesale_catalog_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Catalog downloaded successfully!", { id: toastId });
    } catch (error) {
      console.error('Download error:', error);
      toast.error("Failed to download catalog. Please try again.");
    } finally {
      setDownloadingCatalog(false);
    }
  };




  const handleReturnSearch = async () => {
    if (!returnQuery) return;
    try {
      // Try searching by exact order number or ID
      // First attempt: fetch directly if it looks like an ID
      // Or use a search endpoint if created. Since we don't have a dedicated search for returns,
      // let's try the direct fetch by ID first.
      let order;
      try {
        const res = await api.get(`/admin/orders/${returnQuery.trim()}`);
        order = res.data;
      } catch (e) {
        // If not found by ID, maybe it's an order number? We can fetch all and filter or add search endpoint.
        // For now assume ID or robust ID search from existing admin endpoints.
        toast.error("Order not found");
        return;
      }

      if (order) {
        setReturnOrder(order);
        setReturnItems({});
      }
    } catch (e) {
      toast.error("Order search failed");
    }
  };

  const submitReturn = async () => {
    if (!returnOrder) return;
    const items = Object.entries(returnItems)
      .filter(([_, qty]) => qty > 0)
      .map(([pid, qty]) => ({ product_id: pid, quantity: Number(qty) }));

    if (items.length === 0) {
      toast.error("No items selected for return");
      return;
    }

    try {
      await posAPI.returnItems({
        order_id: returnOrder.id,
        items: items
      });
      toast.success("Return processed successfully");
      setShowReturnDialog(false);
      setReturnOrder(null);
      setReturnItems({});
      setReturnQuery('');
    } catch (e) {
      console.error(e);
      toast.error("Failed to process return");
    }
  };


  // ... (inside component)

  // ... handleCheckout, printInvoice ... 


  const handleCheckout = async (paymentConfirmed = false, isQuotation = false) => {
    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    const belowMinItem = enforceMinQty
      ? cart.find((item) => item.quantity < (item.product.wholesale_min_qty || 10))
      : null;
    if (belowMinItem) {
      const minQty = belowMinItem.product.wholesale_min_qty || 10;
      toast.error(`${belowMinItem.product.name}: minimum quantity is ${minQty}`);
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        status: isQuotation ? 'quotation' : undefined,
        is_quotation: isQuotation,
        quotation_id: isQuotation ? null : loadedQuotationId,
        items: cart.map(item => {
          const price = getWholesalePrice(item.product);
          const itemDiscount = discountDetails.perItemDiscount[item.product.id] || 0;
          const itemTakeaway = getTakeawayDetails().perItemTakeaway[item.product.id] || 0;
          return {
            product_id: item.product.id,
            quantity: item.quantity,
            product_name: item.product.name,
            sku: item.product.sku,
            price: Number(price),
            discount: itemDiscount,
            takeaway_discount: itemTakeaway,
            total: (Number(price) * Number(item.quantity)) - itemDiscount - itemTakeaway,
            image_url: item.product.images?.[0] || null,
            gst_rate: item.product.gst_rate || 18,
          };
        }),
        shipping_address: {
          name: customer?.name || newCustomer.name || user?.name || 'Wholesale Customer',
          phone: customer?.phone || newCustomer.phone || user?.phone || 'N/A',
          line1: 'Wholesale Purchase',
          city: 'Local',
          state: 'Local',
          pincode: '000000',
        },
        payment_method: paymentMethod,
        is_offline: true,
        customer_phone: customer?.phone || newCustomer.phone || user?.phone || null,
        apply_gst: applyGST,
        subtotal: getSubtotal(),
        gst_total: getGST(),
        grand_total: getTotal(),
        discount_amount: discountType === 'flat' ? discountDetails.totalDiscount : 0,
        discount_percentage: discountType === 'percentage' ? parseFloat(discountValue || 0) : 0,
        notes: getTakeawayAmount() > 0 ? [{ type: 'takeaway_discount', amount: getTakeawayAmount() }] : []
      };

      let response;
      if (isEditing) {
        response = await posAPI.updateSale(loadedQuotationId, orderData);
        toast.success(`Order #${response.data.order_number} updated!`);
      } else if (isQuotation) {
        // Use wholesale quotation endpoint (doesn't require POS access)
        response = await posAPI.createWholesaleQuotation(orderData);
        toast.success(`✓ Quotation sent to Amorlias! Order #${response.data.order_number}`);
      } else {
        // Use wholesale sale endpoint (creates WHS orders)
        response = await posAPI.createWholesaleSale(orderData);
        toast.success(`Wholesale sale completed! Order #${response.data.order_number}`);
      }

      // Store last order for invoice dialog
      setLastOrder(response.data);

      // Show invoice dialog
      setShowInvoiceDialog(true);
      setShowQRDialog(false);

      // Reset
      setCart([]);
      setDiscountValue('');
      setDiscountType('none');
      setTakeawayDiscount('');
      setLoadedQuotationId(null);
      setIsEditing(false);
      fetchProducts(currentPage);

      // Activate Cooldown
      if (!isQuotation) {
        setIsButtonDisabled(true);
        setCountdown(10);
      }
    } catch (error) {
      console.error('Sale/Quotation Error:', error);
      toast.error(getApiErrorMessage(error, 'Failed to complete sale'));
    } finally {
      setLoading(false);
    }
  };

  const printInvoice = () => {
    if (!lastOrder) return;
    generatePrintInvoice(lastOrder, printSize);
  };

  return (
    <div className="h-[calc(100vh-80px)] flex gap-6" data-testid="admin-pos">
      {/* Products Section */}
      <div className="flex-1 flex flex-col">
        {/* Header Section - Sticky */}
        <div className="flex-shrink-0 space-y-2">
          <div className="mb-2">
            <h1 className="text-2xl font-bold text-primary">Wholesale Portal</h1>
            <div className="flex items-center gap-2">
              <p className="text-slate-400">Create wholesale quotations and orders</p>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadCatalog}
                disabled={downloadingCatalog}
                className="ml-4 border-primary/40 hover:bg-primary/10 text-primary"
                title="Download category-wise product catalog"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloadingCatalog ? 'Downloading...' : 'Download Catalog'}
              </Button>
              {user?.role === 'admin' && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowFetchDialog(true)}
                    className="ml-2 border-slate-600 hover:bg-slate-700"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Fetch Order/Qt
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowReturnDialog(true)}
                    className="ml-2 border-slate-600 hover:bg-slate-700 text-orange-400"
                  >
                    <RefreshCcw className="w-4 h-4 mr-2" />
                    Return Items
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/admin/orders")}
                    className="ml-2 border-slate-600 hover:bg-slate-700 text-yellow-400"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Orders
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  setRefreshing(true);
                  await fetchProducts(currentPage, search);
                  setRefreshing(false);
                }}
                className="h-6 w-6 ml-2 text-slate-400 hover:text-white"
                title="Refresh Products"
              >
                <RefreshCcw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Pagination Info */}
          <div className="text-sm text-slate-400 flex items-center gap-4">
            <span>Showing {products.length} of {totalProducts} products (Page {currentPage} of {totalPages})</span>
            {totalPages > 1 && (
              <span className="text-xs text-slate-500">
                Tip: Use Ctrl+← / Ctrl+→ for quick navigation
              </span>
            )}
          </div>

          {/* Scan Input */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Scan className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                ref={scanInputRef}
                placeholder="Scan barcode, SKU or Name..."
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                onKeyDown={handleScanInput}
                className="pl-10 input-admin"
                data-testid="scan-input"
              />
            </div>
            <Button variant="outline" className="border-slate-600" onClick={() => scanInputRef.current?.focus()}>
              <QrCode className="w-4 h-4" />
            </Button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 input-admin"
              data-testid="pos-search"
            />
          </div>
        </div>

        {/* Scrollable Products Grid */}
        <div className="flex-1 overflow-auto scrollbar-invisible">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {products.map((product) => {
              const unavailable = isUnavailable(product);
              return (
              <Card
                key={product.id}
                className={`bg-slate-800 border-slate-700 transition-all ${unavailable
                  ? 'opacity-50 cursor-not-allowed'
                  : 'cursor-pointer hover:border-primary'
                  }`}
                onClick={unavailable ? undefined : () => addToCart(product)}
                aria-disabled={unavailable}
                title={unavailable
                  ? (getStock(product) <= 0 ? 'Out of stock' : `Only ${getStock(product)} in stock (min qty ${getMinQty(product)})`)
                  : undefined}
                data-testid={`pos-product-${product.id}`}
              >
                <CardContent className="p-3">
                  <div className="aspect-square bg-slate-700 rounded-lg mb-2 overflow-hidden">
                    {product.images?.[0] && (
                      <img src={getImageUrl(product.images[0])} alt="" className="w-full h-full object-cover" />
                    )}
                  </div>
                  <h4 className="font-medium text-sm truncate">{product.name}</h4>
                  <p className="text-xs text-slate-400 font-mono">{product.sku}</p>
                  <div className="flex justify-between items-center mt-2">
                    <div>
                      <span className="font-bold text-primary">₹{product.wholesale_price || product.selling_price}</span>
                      <span className="text-[10px] text-slate-400 block">Min Qty: {product.wholesale_min_qty || 10}</span>
                    </div>
                    <Badge className={unavailable ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}>
                      {getStock(product) <= 0 ? 'Out' : product.stock_qty ?? 'In stock'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>

        {/* Footer Section - Sticky Pagination */}
        <div className="flex-shrink-0 border-t border-slate-700 bg-slate-900">
          <div className="flex flex-col md:flex-row justify-between items-center gap-2">
            {/* Items per page */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">Items per page:</span>
              <Select
                value={limit.toString()}
                onValueChange={(val) => setLimit(Number(val))}
              >
                <SelectTrigger className="w-[70px] h-8 bg-slate-800 border-slate-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="80">80</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Pagination Buttons */}
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchProducts(currentPage - 1)}
                  disabled={currentPage <= 1}
                  className="border-slate-600 px-3 h-8 text-xs"
                  title="Previous page (Ctrl+←)"
                >
                  <ChevronLeft className="w-3 h-3 mr-1" />
                  Prev
                </Button>

                <div className="flex items-center gap-2 px-1">
                  <span className="text-sm text-slate-400 whitespace-nowrap">
                    Page {currentPage} of {totalPages}
                  </span>

                  {/* Jump to page */}
                  <div className="flex items-center gap-2">
                    <Input
                      className="w-16 h-8 text-center bg-slate-800 border-slate-700 px-2 text-sm"
                      placeholder="Page"
                      value={inputPage}
                      onChange={(e) => setInputPage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const pageNum = parseInt(inputPage);
                          if (pageNum >= 1 && pageNum <= totalPages) {
                            fetchProducts(pageNum);
                            setInputPage('');
                          }
                        }
                      }}
                      title="Enter page number and press Enter"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2 text-slate-400 hover:text-white text-xs"
                      disabled={!inputPage}
                      onClick={() => {
                        const pageNum = parseInt(inputPage);
                        if (pageNum >= 1 && pageNum <= totalPages) {
                          fetchProducts(pageNum);
                          setInputPage('');
                        }
                      }}
                      title="Go to page"
                    >
                      Go
                    </Button>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchProducts(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="border-slate-600 px-3 h-8 text-xs"
                  title="Next page (Ctrl+→)"
                >
                  Next
                  <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fetch Dialog */}
      <Dialog open={showFetchDialog} onOpenChange={setShowFetchDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader><DialogTitle>Fetch Order / Quotation</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <Label>Order ID (UUID) or Number</Label>
            <Input value={quotationId} onChange={e => setQuotationId(e.target.value)} placeholder="e.g. 193bae..." className="input-admin" />
            <Button onClick={fetchQuotation} className="w-full btn-primary" disabled={loading}>{loading ? 'Loading...' : 'Load Order'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={showReturnDialog} onOpenChange={setShowReturnDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl">
          <DialogHeader><DialogTitle>Return Items</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input value={returnQuery} onChange={e => setReturnQuery(e.target.value)} placeholder="Enter Order ID to Search" className="input-admin" />
              <Button onClick={handleReturnSearch}>Search</Button>
            </div>

            {returnOrder && (
              <div className="mt-4">
                <h4 className="font-bold mb-2">Order #{returnOrder.order_number}</h4>
                <Table>
                  <TableHeader><TableRow><TableHead>Product</TableHead><TableHead>Price</TableHead><TableHead>Sold Qty</TableHead><TableHead>Return Qty</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {returnOrder.items.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell>₹{item.price}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            max={item.quantity}
                            value={returnItems[item.product_id] || ''}
                            onChange={(e) => setReturnItems({ ...returnItems, [item.product_id]: Number(e.target.value) })}
                            className="w-20 bg-slate-700 border-slate-600"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Button onClick={submitReturn} className="w-full mt-4 bg-red-600 hover:bg-red-700">Process Return</Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Cart Section */}
      <Card className="w-[420px] bg-slate-800 border-slate-700 flex flex-col">
        <CardHeader className="border-b border-slate-700 py-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShoppingCart className="w-5 h-5" />
            Current Sale
            {cart.length > 0 && <Badge className="ml-2">{cart.reduce((s, i) => s + i.quantity, 0)}</Badge>}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col p-4 overflow-hidden">
          {/* Customer Search */}
          {user?.role === 'admin' && (
            <div className="mb-4">
              <Label className="text-xs text-slate-400 mb-1 block">Customer Search</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Phone number or name"
                  value={customerQuery}
                  onChange={(e) => setCustomerQuery(e.target.value)}
                  className="input-admin"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      searchCustomer();
                    }
                  }}
                />
                <Button
                  variant="outline"
                  className="border-slate-600"
                  onClick={searchCustomer}
                  title="Search Customer"
                >
                  <User className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  className="border-slate-600"
                  onClick={() => {
                    setShowAddCustomer(true);
                    setNewCustomer({ name: '', phone: '', email: '' }); // Reset form
                  }}
                  title="Add New Customer"
                >
                  <UserPlus className="w-4 h-4" />
                </Button>
              </div>
              {customer && (
                <div className="mt-2 p-2 bg-slate-700/50 rounded text-sm flex justify-between items-center">
                  <div>
                    <p className="font-medium">{customer.name}</p>
                    <p className="text-xs text-slate-400">{customer.phone}</p>
                    {customer.is_seller && (
                      <Badge className="mt-1 bg-primary/10 text-primary text-xs">Seller - Wholesale Prices</Badge>
                    )}
                  </div>
                  <Button size="icon" variant="ghost" className="w-6 h-6" onClick={() => {
                    setCustomer(null);
                    setCustomerQuery('');
                  }}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Customer Display for Wholesaler */}
          {user?.role !== 'admin' && (
            <div className="mb-4">
              <Label className="text-xs text-slate-400 mb-1 block">Customer</Label>
              <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600/50 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-lg">
                  {user?.name?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white truncate">{user?.name}</p>
                  <p className="text-xs text-slate-400 truncate">{user?.phone || user?.email}</p>
                </div>
                <Badge variant="secondary" className="bg-primary/10 text-primary text-[10px] whitespace-nowrap">
                  Wholesale
                </Badge>
              </div>
            </div>
          )}

          {/* Cart Items */}
          <div ref={cartScrollRef} className="flex-1 overflow-auto scrollbar-invisible space-y-2">
            {cart.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Cart is empty</p>
                <p className="text-sm">Scan or click products to add</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700">
                    <TableHead className="text-slate-400 text-xs">Item</TableHead>
                    <TableHead className="text-slate-400 text-xs text-center">Qty</TableHead>
                    <TableHead className="text-slate-400 text-xs text-right">Price</TableHead>
                    <TableHead className="w-8"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cart.map((item) => {
                    // Use the same pricing logic as getSubtotal()
          const price = getWholesalePrice(item.product);
                    const itemDiscount = discountDetails.perItemDiscount[item.product.id] || 0;
                    return (
                      <TableRow key={item.product.id} className="border-slate-700">
                        <TableCell className="py-2">
                          <p className="font-medium text-sm truncate max-w-[120px]">{item.product.name}</p>
                          <p className="text-xs text-slate-400">{item.product.sku}</p>
                        </TableCell>
                        <TableCell className="py-2">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="w-6 h-6"
                              onClick={() => {
                                if (enforceMinQty) {
                                  updateQuantity(item.product.id, item.quantity - 1);
                                } else {
                                  updateQuantity(item.product.id, Math.max(0, item.quantity - 1));
                                }
                              }}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="w-6 text-center text-sm">{item.quantity}</span>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="w-6 h-6"
                              disabled={item.quantity >= getStock(item.product)}
                              title={item.quantity >= getStock(item.product) ? `Only ${getStock(item.product)} in stock` : undefined}
                              onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="py-2 text-right">
                          {(itemDiscount > 0 || getTakeawayDetails().perItemTakeaway[item.product.id] > 0) ? (
                            <div className="flex flex-col items-end">
                              <span className="text-xs text-slate-400 line-through">₹{(price * item.quantity).toFixed(0)}</span>
                              <span className="font-medium text-emerald-400">₹{((price * item.quantity) - itemDiscount - (getTakeawayDetails().perItemTakeaway[item.product.id] || 0)).toFixed(2).replace(/\.00$/, '')}</span>
                              <p className="text-[10px] text-emerald-400/80">(Disc ₹{itemDiscount.toFixed(2)}, TA ₹{(getTakeawayDetails().perItemTakeaway[item.product.id] || 0).toFixed(2)})</p>
                            </div>
                          ) : (
                            <span className="font-medium">₹{(price * item.quantity).toFixed(0)}</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2">
                          <Button size="icon" variant="ghost" className="w-6 h-6 text-red-400" onClick={() => removeFromCart(item.product.id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Scroll sentinel removed — container ref handles scrolling */}
                </TableBody>
              </Table>
            )}
          </div>

          {/* Billing Options */}
          {cart.length > 0 && (
            <div className="border-t border-slate-700 pt-4 mt-4 space-y-3">


              {/* Discount */}
              {user?.role === 'admin' && (
                <div className="flex gap-2">
                  <Select value={discountType} onValueChange={setDiscountType}>
                    <SelectTrigger className="w-32 input-admin h-9">
                      <SelectValue placeholder="Discount" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Discount</SelectItem>
                      <SelectItem value="percentage">% Discount</SelectItem>
                      <SelectItem value="flat">Flat ₹</SelectItem>
                    </SelectContent>
                  </Select>
                  {discountType !== 'none' && (
                    <Input
                      type="number"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      placeholder={discountType === 'percentage' ? '%' : '₹'}
                      className="input-admin h-9"
                    />
                  )}
                </div>
              )}

              {user?.role === 'admin' && (
                <div className="flex gap-2 mt-2">
                  <div className="flex items-center w-32 px-3 bg-slate-800 border border-slate-700 rounded-md text-sm text-slate-400 shrink-0 h-9">
                    Take away: ₹
                  </div>
                  <Input
                    type="number"
                    value={takeawayDiscount}
                    onChange={(e) => setTakeawayDiscount(e.target.value)}
                    placeholder="Discount amount"
                    className="input-admin h-9"
                  />
                </div>
              )}

              {/* Totals */}
              <div className="space-y-1 text-sm bg-slate-700/50 p-3 rounded-lg">
                <div className="flex justify-between">
                  <span className="text-slate-400">Subtotal</span>
                  <span>₹{getSubtotal().toFixed(2)}</span>
                </div>
                {applyGST && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">GST (18%)</span>
                    <span>₹{getGST().toFixed(2)}</span>
                  </div>
                )}
                {discountDetails.totalDiscount > 0 && (
                  <div className="flex justify-between text-emerald-400">
                    <span>Discount</span>
                    <span>-₹{discountDetails.totalDiscount.toFixed(2)}</span>
                  </div>
                )}
                {getTakeawayAmount() > 0 && (
                  <div className="flex justify-between text-emerald-400">
                    <span>Take Away Discount</span>
                    <span>-₹{getTakeawayAmount().toFixed(2)}</span>
                  </div>
                )}
                {getRequestedDiscount() > discountDetails.totalDiscount && (
                  <div className="text-[11px] text-amber-400">
                    Discount capped by product max discount limits
                  </div>
                )}
                <Separator className="my-2 bg-slate-600" />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-primary">₹{getTotal().toFixed(2)}</span>
                </div>
              </div>

              {/* Payment Method */}
              {user?.role === 'admin' && (
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="input-admin">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">
                      <div className="flex items-center gap-2">
                        <Banknote className="w-4 h-4" />
                        Cash
                      </div>
                    </SelectItem>
                    <SelectItem value="upi">
                      <div className="flex items-center gap-2">
                        <QrCode className="w-4 h-4" />
                        UPI / QR
                      </div>
                    </SelectItem>
                    <SelectItem value="card">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Card
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2">
                {paymentMethod === 'upi' && user?.role === 'admin' && ( // Hide QR for quotation
                  <Button
                    variant="outline"
                    className="border-slate-600"
                    onClick={generateQR}
                  >
                    <QrCode className="w-4 h-4 mr-2" />
                    Show QR
                  </Button>
                )}

                {user?.role === 'admin' ? (
                  // Admin -> Sale Only
                  <Button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-11"
                    onClick={() => handleCheckout()}
                    disabled={loading || isButtonDisabled}
                  >
                    {loading ? 'Processing...' : (isButtonDisabled ? `Wait ${countdown}s` : 'Complete Sale')}
                  </Button>
                ) : (
                  // Wholesaler / POS User -> Quotation Only (as per request)
                  <Button
                    className="flex-1 bg-blue-600 hover:bg-blue-700 h-11"
                    onClick={() => handleCheckout(false, true)} // isQuotation = true
                    disabled={loading}
                    data-testid="pos-quotation-btn"
                  >
                    {loading ? 'Processing...' : `Create Quotation`}
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* QR Dialog */}
      <Dialog open={showQRDialog} onOpenChange={setShowQRDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-sm">
          <DialogHeader>
            <DialogTitle>Scan to Pay</DialogTitle>
            <DialogDescription className="text-sm text-slate-400">Scan this QR code to complete the payment.</DialogDescription>
          </DialogHeader>
          {qrData && (
            <div className="text-center">
              <div className="bg-white p-4 rounded-lg inline-block mb-4">
                {qrData.qr_code ? (
                  <img
                    src={qrData.qr_code}
                    alt="Payment QR Code"
                    className="w-48 h-48"
                  />
                ) : (
                  <div className="w-48 h-48 bg-gray-100 flex items-center justify-center">
                    <QrCode className="w-24 h-24 text-gray-400" />
                  </div>
                )}
              </div>
               <p className="text-xl font-bold text-primary mb-2">₹{Number(qrData.amount || 0).toFixed(2)}</p>
              <p className="text-sm text-slate-400 mb-4">UPI ID: {qrData.upi_id}</p>
              <Button onClick={() => handleCheckout(true)} className="w-full bg-emerald-500 hover:bg-emerald-600">
                Payment Received - Complete Sale
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              {lastOrder?.status === 'quotation' ? (
                <>
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-emerald-400" />
                  </div>
                  <span className="text-emerald-400">Quotation Sent Successfully!</span>
                </>
              ) : (
                <>
                  <Receipt className="w-5 h-5" />
                  Sale Complete!
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {lastOrder && (
            <div className="space-y-4">
              {/* Order Summary */}
              <div className="bg-gradient-to-br from-slate-700/50 to-slate-700/30 p-5 rounded-xl border border-slate-600">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Order Number</p>
                    <p className="text-2xl font-bold text-white mt-1">{lastOrder.order_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Total Amount</p>
                    <p className="text-3xl font-bold text-emerald-400 mt-1">₹{Number(lastOrder.grand_total || 0).toFixed(2)}</p>
                  </div>
                </div>

                {lastOrder.items && lastOrder.items.length > 0 && (
                  <div className="pt-3 border-t border-slate-600">
                    <p className="text-xs text-slate-400 mb-2">{lastOrder.items.length} item(s) in this order</p>
                  </div>
                )}
              </div>

              {/* Print Size Selector */}
              <div className="bg-slate-700/30 p-4 rounded-lg border border-slate-600">
                <Label className="text-sm text-slate-300 mb-2 block">Print Size</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={printSize === 'a4' ? 'default' : 'outline'}
                    className={`${printSize === 'a4' ? 'bg-primary hover:bg-primary/90' : 'border-slate-600 hover:bg-slate-700'}`}
                    onClick={() => setPrintSize('a4')}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    A4 Size (with images)
                  </Button>
                  <Button
                    variant={printSize === 'thermal' ? 'default' : 'outline'}
                    className={`${printSize === 'thermal' ? 'bg-primary hover:bg-primary/90' : 'border-slate-600 hover:bg-slate-700'}`}
                    onClick={() => setPrintSize('thermal')}
                  >
                    <Receipt className="w-4 h-4 mr-2" />
                    Thermal (compact)
                  </Button>
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  {printSize === 'a4' ? 'Standard A4 invoice with product images' : 'Compact thermal printer format (58mm/80mm)'}
                </p>
              </div>

              {/* Quotation Specific Message */}
               {lastOrder?.status === 'quotation' && (
                <div className="space-y-3">
                  {/* Success Message */}
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-emerald-300 mb-1">Your quotation has been sent to Amorlias!</p>
                        <p className="text-sm text-slate-300">
                          Download your quotation below and share it with Amorlias to confirm your order.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Next Steps */}
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <p className="font-semibold text-blue-300 mb-2 flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Next Steps:
                    </p>
                    <ol className="text-sm text-slate-300 space-y-2 ml-7">
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400 font-bold">1.</span>
                        <span>Print your quotation using the button below</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400 font-bold">2.</span>
                        <span>Share it with Amorlias via WhatsApp or email</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-blue-400 font-bold">3.</span>
                        <span>Visit the pickup location to collect your order</span>
                      </li>
                    </ol>
                  </div>

                  {/* Pickup Location */}
                  <div className="bg-slate-700/50 rounded-lg p-4 border border-slate-600">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="font-bold text-white mb-2">Pickup Location</p>
                        <div className="text-sm text-slate-300 space-y-1">
                          <p className="font-semibold text-white">
                            {settings?.business_name || settings?.company_name || 'Amorlias Mart'}
                          </p>
                          {settings?.address ? (
                            <>
                              {settings.address.line1 && <p>{settings.address.line1}</p>}
                              {settings.address.line2 && <p>{settings.address.line2}</p>}
                              <p>
                                {settings.address.city && settings.address.city}
                                {settings.address.city && settings.address.state && ', '}
                                {settings.address.state && settings.address.state}
                                {settings.address.pincode && ` - ${settings.address.pincode}`}
                              </p>
                            </>
                          ) : (
                            <>
                              <p>Amorlias, Orchid Mall , Thana Mode</p>
                              <p>Chomu(Jaipur), India ,303704</p>
                            </>
                          )}
                          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-600">
                            <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <a
                              href={`tel:${settings?.phone || settings?.whatsapp_number}`}
                              className="font-semibold text-emerald-400 hover:text-emerald-300"
                            >
                              {settings?.phone || settings?.whatsapp_number}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={printInvoice}
                  className="flex-1 h-11"
                  variant="outline"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Receipt
                </Button>
                <Button
                  onClick={() => {
                    setShowInvoiceDialog(false);
                    setLastOrder(null);
                  }}
                  className="flex-1 bg-primary hover:bg-primary/90 h-11"
                >
                   {lastOrder?.status === 'quotation' ? 'Create New Quotation' : 'New Sale'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={showAddCustomer} onOpenChange={setShowAddCustomer}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle>Add Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                placeholder="Customer name"
                className="input-admin"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                placeholder="Phone number"
                className="input-admin"
              />
            </div>
            <div className="space-y-2">
              <Label>Email (Optional)</Label>
              <Input
                value={newCustomer.email}
                onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                placeholder="email@example.com"
                className="input-admin"
              />
            </div>
            <Button
              onClick={() => {
                setCustomerQuery(newCustomer.phone);
                setCustomer(newCustomer);
                setShowAddCustomer(false);
                toast.success('Customer info added');
              }}
              className="w-full bg-primary"
            >
              Add Customer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
